import React, { useMemo, type FC } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import dayjs from 'dayjs';

import CurrentStreakSection from 'src/pages/landlord/activity/CurrentStreakSection';
import CurrentWeekSection from 'src/pages/landlord/activity/CurrentWeekSection';
import MonthlyCalendarSection from 'src/pages/landlord/activity/MonthlyCalendarSection';
import { useUserStreaks } from 'src/services/api';

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const getMondayFirstIndex = (date: dayjs.Dayjs) => (date.day() + 6) % 7;

const ActivityPage: FC = () => {
    const { data: streaks, isLoading: isLoadingStreaks } = useUserStreaks();
    const now = dayjs(new Date().toISOString().slice(0, 10));

    const activityDaySet = useMemo(() => {
        const list = streaks?.activities ?? [];
        return new Set(list.map((a) => a.created_at));
    }, [streaks]);

    const weekCompletion = useMemo(() => {
        const today = now.startOf('day');
        const mondayOffset = getMondayFirstIndex(today);
        const startOfWeek = today.subtract(mondayOffset, 'day');

        return Array.from({ length: 7 }, (_, i) => {
            const d = startOfWeek.add(i, 'day');
            return activityDaySet.has(d.format('YYYY-MM-DD'));
        });
    }, [activityDaySet, now]);

    if (isLoadingStreaks || streaks === undefined) {
        return <LinearProgress />;
    }

    return (
        <Box p={2} display="flex" flexDirection="column" gap={2}>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }} gap={2}>
                <CurrentStreakSection streakWeeks={streaks.user_streak?.streak_count ?? 0} />
                <CurrentWeekSection
                    weekdayLabels={WEEKDAY_LABELS}
                    weekCompletion={weekCompletion}
                />
            </Box>

            <MonthlyCalendarSection
                currentDate={now}
                weekdayLabels={WEEKDAY_LABELS}
                activityDaySet={activityDaySet}
            />
        </Box>
    );
};

export default ActivityPage;
